import { ZAttrMeta, ZTypeDef } from "src/business/attribute";

type PresetDef = {
  key: string;
  label: string;
  code: Omit<ZAttrMeta, "displayId" | "name" | "title" | "uid">;
  arrayAlt?: boolean; // альтернативный вариант массива
};

type MultiMode = "multi" | "single";
type FileMode = "file" | "image";
const isImage = (fmode: FileMode): boolean => fmode === "image";

const condType = (
  type: ZTypeDef,
  mode: MultiMode,
  maxCount?: number,
): ZTypeDef =>
  mode === "single" ? type : { name: "array", maxCount, item: { type } };

const fileItem = (fmode: FileMode, mode: MultiMode): PresetDef["code"] => ({
  type: condType({ name: "file", image: isImage(fmode) }, mode, 1),
  view: {
    component: isImage(fmode)
      ? {
          type: "Image",
          maxWidth: 100,
          maxHeight: 100,
        }
      : {
          type: "Files",
        },
  },
  edit: {
    component: {
      type: "Uploader",
      isImage: isImage(fmode),
      accept: isImage(fmode) ? "image/*" : undefined, // См. https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept#unique_file_type_specifiers
      maxSize: 15,
    },
    rules: [{ type: "file" }],
  },
});

const extImage = (mode: MultiMode): PresetDef["code"] => ({
  type: condType({ name: "strOption", role: "image" }, mode, 1),
  view: {
    component: {
      type: "Image",
      maxWidth: 100,
      maxHeight: 100,
    },
  },
  edit: {
    component: {
      type: "AttrExternalImages",
    },
  },
});

export const presets: PresetDef[] = [
  {
    key: "realNumber",
    label: "Дробное число",
    code: {
      type: { name: "number" },
    },
  },
  {
    key: "intNumber",
    label: "Целое число",
    code: {
      type: { name: "number", precision: 0 },
    },
  },
  {
    key: "selectInline",
    label: "Выбор из локального списка",
    code: {
      type: { name: "strOption" },
      edit: {
        component: {
          type: "Select",
          options: {
            type: "inline",
            list: [
              { id: "first", label: "First" },
              { id: "second", label: "Second" },
            ],
          },
          search: "part",
        },
      },
    },
  },
  {
    key: "selectValuesCatalog",
    label: "Выбор из каталога значений в списке",
    code: {
      type: {
        name: "strOption",
      },
      edit: {
        component: {
          type: "Select",
          options: {
            type: "valuesCatalog",
            valuesCatalogId: "",
            labelTemplate: "",
          },
          search: "part",
        },
      },
      view: null,
    },
  },
  {
    key: "selectValuesModal",
    label: "Выбор из каталога значений в окне",
    code: {
      type: { name: "strOption" },
      edit: {
        component: {
          type: "SelectValuesModal",
          valuesCatalogId: "",
          labelTemplate: "",
        },
      },
      view: null,
    },
  },
  {
    key: "record",
    label: "Ссылка на запись",
    code: {
      type: {
        name: "strOption",
        role: "record",
      },
      edit: {
        component: {
          type: "RecordSelect",
          catalogId: "",
        },
      },
    },
  },
  {
    key: "records",
    label: "Массив ссылок на записи",
    code: {
      type: {
        name: "array",
        item: {
          type: {
            name: "strOption",
            role: "record",
          },
        },
      },
      edit: {
        component: {
          type: "RecordSelect",
          catalogId: "",
        },
      },
    },
  },
  {
    key: "externalImages",
    label: "Внешние изображения",
    code: extImage("multi"),
  },
  {
    key: "externalImage",
    label: "Внешнее изображение",
    code: extImage("single"),
    arrayAlt: true,
  },
  {
    key: "internalImages",
    label: "Внутренние изображения",
    code: fileItem("image", "multi"),
  },
  {
    key: "internalImage",
    label: "Внутреннее изображение",
    code: fileItem("image", "single"),
    arrayAlt: true,
  },
  {
    key: "files",
    label: "Файлы",
    code: fileItem("file", "multi"),
  },
  {
    key: "file",
    label: "Файл",
    code: fileItem("file", "single"),
    arrayAlt: true,
  },
];
